import { useCallback } from 'react'

import { useNavigate, useSearch } from '@tanstack/react-router'

import { useAvailableIntegrationsMapSync } from '@/features/integrations/queries'

import { toggleFilterValue, withFilterGroup } from '../lib/case-filters'

import type { CaseFilterKey, CaseFilters } from '../lib/case-filter-groups'
import type { CaseDateField, CaseListSearch } from '../lib/case-filters'
import type { CaseFilterState } from '../lib/case-list-params'

const NO_FILTERS: CaseFilters = {}

/**
 * The Case filters in the URL, and the writes that change them. Both Case list
 * routes validate the same schema (`caseListSearchSchema`), so this reads the
 * search loosely and serves either route.
 *
 * Source values are connector definition ids, as the URL holds them;
 * `useStrictCaseListUrl` removes one that is not a Source option.
 *
 * The catalog, for the Source chips' names, is read without suspending. The
 * chips render inside the page skeleton, which is itself a `Suspense` fallback
 * — a fallback that suspends hands the wait to the boundary above it.
 */
export const useCaseFilters = () => {
	const navigate = useNavigate()
	const search = useSearch({ strict: false }) as Pick<
		CaseListSearch,
		'filters' | 'openedAfter' | 'openedBefore' | 'q'
	>
	const catalog = useAvailableIntegrationsMapSync()

	const filters = search.filters ?? NO_FILTERS
	/** The filter state as `toCaseListParams` and the counts take it. */
	const state: CaseFilterState = {
		filters,
		openedAfter: search.openedAfter,
		openedBefore: search.openedBefore,
		q: search.q,
	}

	const writeSearch = useCallback(
		(patch: Record<string, unknown>) => {
			navigate({
				to: '.',
				search: (prev) => {
					// Router's typed search shape differs per route; `undefined`
					// removes a param.
					const next: Record<string, unknown> = { ...prev, ...patch }
					return next
				},
			})
		},
		[navigate],
	)

	/** Flips `value` in group `key`. Returns whether it added the value. */
	const toggle = useCallback(
		(key: CaseFilterKey, value: string): boolean => {
			const current: ReadonlyArray<string> = filters[key] ?? []
			writeSearch({
				filters: withFilterGroup(
					filters,
					key,
					toggleFilterValue(current, value),
				),
			})
			return !current.includes(value)
		},
		[filters, writeSearch],
	)

	const setDate = useCallback(
		(field: CaseDateField, value: string | undefined) =>
			writeSearch({ [field]: value }),
		[writeSearch],
	)

	/** Every filter and both dates. The search query is not a filter; it stays. */
	const clearAll = useCallback(
		() =>
			writeSearch({
				filters: undefined,
				openedAfter: undefined,
				openedBefore: undefined,
			}),
		[writeSearch],
	)

	return {
		state,
		/** `state.filters`, never `undefined`. */
		filters,
		catalog,
		toggle,
		setDate,
		clearAll,
	}
}
